import { useState } from 'react';
import { gql } from '../__generated__';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { Alert, AlertTitle, Box, Button, Chip, Collapse, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Close, ContentCopy, Key, Sync } from '@mui/icons-material';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmDialog from '../components/ConfirmDialog';
import { formatDate } from '../Formatters';
import { IdentityProviderSummaryItemFragment } from '../__generated__/graphql';
import { GET_IDENTITY_PROVIDER_SUMMARY } from './Sso.graphql';
import { SsoField, SsoSection } from './SsoSection';
import CopyButton from './CopyButton';
import { isContactStale, STALE_AFTER_HOURS } from './lastContact';

interface Props {
  bksID: number;
  summary: IdentityProviderSummaryItemFragment;
}

const GET_SCIM_PROVISIONING = gql(`
  query GetScimProvisioning($bksID: Int!) {
    getScimProvisioning(bksID: $bksID) {
      id
      licenseId
      enabled
      authoritative
      hasActiveToken
      tokenIssuedAt
    }
  }
`);

const ENABLE_SCIM_PROVISIONING = gql(`
  mutation EnableScimProvisioning($input: EnableScimProvisioningInput!) {
    enableScimProvisioning(input: $input) {
      token
      provisioning {
        id
        licenseId
        enabled
        authoritative
        hasActiveToken
        tokenIssuedAt
      }
    }
  }
`);

const ROTATE_SCIM_TOKEN = gql(`
  mutation RotateScimToken($input: RotateScimTokenInput!) {
    rotateScimToken(input: $input) {
      token
      provisioning {
        id
        licenseId
        enabled
        authoritative
        hasActiveToken
        tokenIssuedAt
      }
    }
  }
`);

const REVOKE_SCIM_TOKEN = gql(`
  mutation RevokeScimToken($input: RevokeScimTokenInput!) {
    revokeScimToken(input: $input) {
      id
      licenseId
      enabled
      authoritative
      hasActiveToken
      tokenIssuedAt
    }
  }
`);

const GET_SCIM_RECONCILIATION_PREVIEW = gql(`
  query GetScimReconciliationPreview($bksID: Int!) {
    getScimReconciliationPreview(bksID: $bksID) {
      people
      accessRows
      sample
    }
  }
`);

const SET_SCIM_AUTHORITATIVE = gql(`
  mutation SetScimAuthoritative($input: SetScimAuthoritativeInput!) {
    setScimAuthoritative(input: $input) {
      id
      licenseId
      enabled
      authoritative
      hasActiveToken
      tokenIssuedAt
    }
  }
`);

const SET_SCIM_PROVISIONING_ENABLED = gql(`
  mutation SetScimProvisioningEnabled($input: SetScimProvisioningEnabledInput!) {
    setScimProvisioningEnabled(input: $input) {
      id
      licenseId
      enabled
      authoritative
      hasActiveToken
      tokenIssuedAt
    }
  }
`);

type HandoverPreview = {
  people: number;
  accessRows: number;
  sample?: readonly (string | null)[] | null;
};

/** One paragraph: ConfirmDialog renders the message in a single Typography, so line breaks collapse. */
function describeHandover(loading: boolean, failed: boolean, preview: HandoverPreview | null | undefined): string {
  if (failed && !loading) {
    return 'Kunne ikke hente hvem som mister tilgangen. Lukk og prøv igjen.';
  }
  if (loading || !preview) {
    return 'Henter oversikt over hvem som mister tilgangen …';
  }

  const consequence =
    'Etter dette får bare brukere som er provisjonert fra kundens brukerkatalog tilgang. Alle som ennå ikke er ' +
    'synkronisert, blir avvist ved innlogging — vent til den første fullstendige synkroniseringen er ferdig.';

  if (preview.people === 0) {
    return `Ingen mister tilgangen nå — alle som har tilgang i dag er provisjonert fra brukerkatalogen. ${consequence}`;
  }

  const names = (preview.sample ?? []).filter((x): x is string => !!x);
  const examples = names.length > 0 ? ` For eksempel: ${names.slice(0, 5).join(', ')}.` : '';

  return (
    `${preview.people} ${preview.people === 1 ? 'person' : 'personer'} ` +
    `(${preview.accessRows} ${preview.accessRows === 1 ? 'tilgang' : 'tilganger'}) mister tilgangen med en gang, ` +
    `fordi brukerkatalogen ikke har provisjonert dem.${examples} ${consequence}`
  );
}

/** "SCIM" only in the explanation and the fields pasted into Entra; elsewhere "synkronisering". */
export default function SsoSyncSection({ bksID, summary }: Props) {
  // The server returns the token once and keeps only its hash; navigating away loses it.
  const [editing, setEditing] = useState(false);
  const [issuedToken, setIssuedToken] = useState<string | undefined>(undefined);
  const [tokenMissing, setTokenMissing] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [confirmAuthoritative, setConfirmAuthoritative] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const { loading, data, refetch } = useQuery(GET_SCIM_PROVISIONING, {
    variables: { bksID },
    fetchPolicy: 'no-cache',
  });

  const refetchSummary = [{ query: GET_IDENTITY_PROVIDER_SUMMARY, variables: { bksID } }];

  const run = (action: () => Promise<unknown>) => {
    setActionError(null);
    action().catch((e: any) => setActionError(e?.message ?? 'Handlingen feilet.'));
  };

  // A missing token means the one chance to capture it is gone; say so rather than show an empty banner.
  const onTokenIssued = (token: string | null | undefined) => {
    setIssuedToken(token ?? undefined);
    setTokenMissing(!token);
    refetch();
  };

  const [enableScim, { loading: enabling }] = useMutation(ENABLE_SCIM_PROVISIONING, {
    refetchQueries: refetchSummary,
    onCompleted: (result) => onTokenIssued(result.enableScimProvisioning?.token),
  });
  const [rotateToken, { loading: rotating }] = useMutation(ROTATE_SCIM_TOKEN, {
    onCompleted: (result) => onTokenIssued(result.rotateScimToken?.token),
  });
  const [revokeToken, { loading: revoking }] = useMutation(REVOKE_SCIM_TOKEN, {
    onCompleted: () => refetch(),
  });
  const [setEnabled, { loading: toggling }] = useMutation(SET_SCIM_PROVISIONING_ENABLED, {
    refetchQueries: refetchSummary,
    onCompleted: () => refetch(),
  });
  const [setAuthoritative, { loading: handingOver }] = useMutation(SET_SCIM_AUTHORITATIVE, {
    refetchQueries: refetchSummary,
    onCompleted: () => refetch(),
  });

  // Loaded on demand: it reads every access row on the licence.
  const [loadPreview, { data: previewData, loading: previewLoading, error: previewError }] = useLazyQuery(GET_SCIM_RECONCILIATION_PREVIEW, {
    fetchPolicy: 'no-cache',
  });
  const preview = previewData?.getScimReconciliationPreview;

  const provisioning = data?.getScimProvisioning;
  const busy = enabling || rotating || revoking || toggling || handingOver;

  const status = !provisioning ? null : !provisioning.enabled ? (
    <Chip size="small" label="Av" />
  ) : !provisioning.hasActiveToken ? (
    <Chip size="small" color="warning" variant="outlined" label="Mangler token" />
  ) : (
    <Chip size="small" color="success" variant="outlined" label="På" />
  );
  const waitingForToken = !!provisioning && provisioning.enabled && !provisioning.hasActiveToken;

  return (
    <SsoSection
      title="Brukersynkronisering"
      icon={<Sync fontSize="small" />}
      status={status}
      action={
        !editing &&
        !loading && (
          <Button size="small" variant="outlined" onClick={() => setEditing(true)}>
            Rediger
          </Button>
        )
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Kundens brukerkatalog (f.eks. Entra ID) holder brukerne på lisensen oppdatert via SCIM.
      </Typography>

      {loading && <LoadingSpinner />}

      <Collapse in={tokenMissing}>
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTokenMissing(false)}>
          Tokenet kom ikke tilbake fra serveren. Prøv «Roter token» for å utstede et nytt.
        </Alert>
      </Collapse>

      <Collapse in={!!actionError}>
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      </Collapse>

      <Collapse in={!!issuedToken}>
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <IconButton size="small" aria-label="Lukk" onClick={() => setIssuedToken(undefined)}>
              <Close fontSize="small" />
            </IconButton>
          }
        >
          <AlertTitle>Nytt token</AlertTitle>
          Gi tokenet til kunden nå. Det vises ikke igjen, og kan bare erstattes — ikke hentes fram.
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
            <strong style={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>{issuedToken}</strong>
            <CopyButton value={issuedToken ?? ''} label="Kopier token" />
          </Stack>
        </Alert>
      </Collapse>

      {!loading && !provisioning && (
        <Stack spacing={2}>
          <Typography variant="body2">Synkronisering er ikke satt opp for denne lisensen.</Typography>
          {editing && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<Key />}
                disabled={busy}
                onClick={() => run(() => enableScim({ variables: { input: { bksID } } }))}
              >
                Sett opp synkronisering
              </Button>
              <Button disabled={busy} onClick={() => setEditing(false)}>
                Avbryt
              </Button>
            </Stack>
          )}
        </Stack>
      )}

      {provisioning && (
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 2fr) repeat(3, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" component="div">
                Tenant URL (limes inn i Entra)
              </Typography>
              {summary.scimTenantUrl ? (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {summary.scimTenantUrl}
                  </Typography>
                  <CopyButton value={summary.scimTenantUrl} label="Kopier Tenant URL" />
                </Stack>
              ) : (
                <Typography variant="body2" color="warning.main">
                  Ikke konfigurert (Scim:BaseUrl mangler)
                </Typography>
              )}
            </Box>
            <SsoField
              label="Token"
              value={
                provisioning.hasActiveToken
                  ? `Utstedt ${provisioning.tokenIssuedAt ? formatDate(provisioning.tokenIssuedAt) : ''}`
                  : 'Ingen aktivt token'
              }
            />
            <SsoField
              label="Synkronisert fra brukerkatalogen"
              value={`${summary.scimActiveUsers} aktive${summary.scimInactiveUsers ? `, ${summary.scimInactiveUsers} deaktivert` : ''}`}
            />
            <SsoField
              label="Sist kontakt med brukerkatalogen"
              value={summary.scimLastContactAt ? formatDate(summary.scimLastContactAt) : 'Ingen ennå'}
            />
          </Box>

          {provisioning.enabled && provisioning.hasActiveToken && isContactStale(summary.scimLastContactAt) && (
            <Alert severity="warning">
              Kundens brukerkatalog har ikke vært i kontakt på over {STALE_AFTER_HOURS} timer. Be kunden sjekke synkroniseringen i Entra — et utløpt
              token eller en synkronisering i karantene er de vanligste årsakene.
            </Alert>
          )}

          {!provisioning.enabled && (
            <Alert severity="warning">
              {provisioning.hasActiveToken
                ? 'Synkroniseringen er slått av. Kundens brukerkatalog avvises, men tokenet beholdes.'
                : 'Synkroniseringen er slått av, og det finnes ikke noe aktivt token. Utsted et token og slå synkroniseringen på igjen for å ta den i bruk.'}
            </Alert>
          )}

          {waitingForToken && (
            <Alert severity={provisioning.authoritative ? 'error' : 'warning'}>
              Tokenet er tilbakekalt, så kundens brukerkatalog kan ikke synkronisere. Utsted et nytt token og gi det til kunden.
              {provisioning.authoritative &&
                ' Siden brukerkatalogen styrer tilgangen, får ingen nye brukere tilgang i mellomtiden, og brukere som fjernes i katalogen beholder tilgangen til kunden har lagt inn det nye tokenet.'}
            </Alert>
          )}

          {editing && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant={waitingForToken ? 'contained' : 'outlined'}
                startIcon={<Key />}
                disabled={busy}
                // Rotating a live token cuts the customer's sync off until they enter the new one.
                onClick={() =>
                  provisioning.hasActiveToken ? setConfirmRotate(true) : run(() => rotateToken({ variables: { input: { id: provisioning.id } } }))
                }
              >
                {provisioning.hasActiveToken ? 'Roter token' : 'Utsted token'}
              </Button>
              <Button
                variant="outlined"
                disabled={busy}
                onClick={() => run(() => setEnabled({ variables: { input: { id: provisioning.id, enabled: !provisioning.enabled } } }))}
              >
                {provisioning.enabled ? 'Slå av synkronisering' : 'Slå på synkronisering igjen'}
              </Button>
              {provisioning.hasActiveToken && (
                <Button variant="outlined" color="error" disabled={busy} onClick={() => setConfirmRevoke(true)}>
                  Tilbakekall token
                </Button>
              )}
            </Stack>
          )}

          {provisioning.enabled && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ pt: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle2" component="h3">
                  {provisioning.authoritative ? 'Brukerkatalogen styrer tilgangen' : 'Innlogging gir fortsatt tilgang'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {provisioning.authoritative
                    ? 'Innlogging gir ikke tilgang. Bare brukere katalogen har provisjonert, har tilgang til lisensen.'
                    : 'Katalogen kan legge til og fjerne brukere, men alle som logger inn får fortsatt tilgang. Bli her til den første synkroniseringen er ferdig.'}
                </Typography>
              </Box>
              {!editing ? null : !provisioning.authoritative && !provisioning.hasActiveToken ? (
                <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                  Utsted et token først
                </Typography>
              ) : (
                <Button
                  variant={provisioning.authoritative ? 'outlined' : 'contained'}
                  color={provisioning.authoritative ? 'warning' : 'primary'}
                  disabled={busy}
                  sx={{ flexShrink: 0 }}
                  onClick={() =>
                    provisioning.authoritative
                      ? run(() => setAuthoritative({ variables: { input: { id: provisioning.id, authoritative: false } } }))
                      : // The dialog shows a failed preview; don't leave the rejection unhandled.
                        (loadPreview({ variables: { bksID } }).catch(() => {}), setConfirmAuthoritative(true))
                  }
                >
                  {provisioning.authoritative ? 'La innlogging gi tilgang igjen' : 'La brukerkatalogen styre tilgangen'}
                </Button>
              )}
            </Stack>
          )}

          {editing && (
            <Stack direction="row">
              <Button disabled={busy} onClick={() => setEditing(false)}>
                Ferdig
              </Button>
            </Stack>
          )}
        </Stack>
      )}

      <ConfirmDialog
        open={confirmAuthoritative}
        title="La brukerkatalogen styre tilgangen?"
        message={describeHandover(previewLoading, !!previewError, preview)}
        disableOK={previewLoading || !!previewError || !preview}
        confirmText="La brukerkatalogen styre"
        deny={() => setConfirmAuthoritative(false)}
        confirm={() => {
          setConfirmAuthoritative(false);
          if (provisioning) {
            run(() => setAuthoritative({ variables: { input: { id: provisioning.id, authoritative: true } } }));
          }
        }}
      />

      <ConfirmDialog
        open={confirmRotate}
        title="Roter token?"
        message={
          'Det gamle tokenet slutter å virke med en gang, og kundens synkronisering stopper til de har lagt inn det nye tokenet i Entra. ' +
          (provisioning?.authoritative ? 'Siden brukerkatalogen styrer tilgangen, får heller ingen nye brukere tilgang i mellomtiden. ' : '') +
          'Det nye tokenet vises bare én gang, så gi det til kunden med en gang.'
        }
        confirmText="Roter token"
        deny={() => setConfirmRotate(false)}
        confirm={() => {
          setConfirmRotate(false);
          if (provisioning) run(() => rotateToken({ variables: { input: { id: provisioning.id } } }));
        }}
      />

      <ConfirmDialog
        open={confirmRevoke}
        title="Tilbakekalle token?"
        message={
          'Kundens synkronisering slutter å virke umiddelbart. Bruk «Roter token» i stedet hvis de skal fortsette med et nytt token.' +
          (provisioning?.authoritative
            ? ' Siden brukerkatalogen styrer tilgangen, får ingen nye brukere tilgang, og brukere som fjernes i katalogen beholder tilgangen, til et nytt token er på plass.'
            : '')
        }
        confirmText="Tilbakekall"
        deny={() => setConfirmRevoke(false)}
        confirm={() => {
          setConfirmRevoke(false);
          if (provisioning) run(() => revokeToken({ variables: { input: { id: provisioning.id } } }));
        }}
      />
    </SsoSection>
  );
}
